import { CaretDownIcon, CaretUpIcon, CheckIcon } from "@phosphor-icons/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Platform, Pressable, StyleProp, StyleSheet, Text, View, ViewStyle } from "react-native";

interface OptionItem {
  label: string;
  value: string;
}

interface CustomDropdownWebProps {
  options: OptionItem[];
  value: string | undefined;
  onChange: (value: string) => void;
  style?: StyleProp<ViewStyle>;
  error?: string;
  openUpward?: boolean;
  disabled?: boolean;
  fullWidth?: boolean;
  placeholder?: string;
  placeholderTextColor?: string;
  accessibilityLabel?: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  dropdownStyle?: StyleProp<ViewStyle>;
  dropdownContainerStyle?: StyleProp<ViewStyle>;
  showArrow?: boolean;
  hideItemBorders?: boolean;
}

interface DropdownMenuPosition {
  left: number;
  width: number;
  top?: number;
  bottom?: number;
}

export const CustomDropdownWeb = ({
  value,
  options,
  onChange,
  style,
  error,
  openUpward = false,
  disabled = false,
  fullWidth = true,
  placeholder = "Select an Option",
  placeholderTextColor,
  accessibilityLabel,
  open: controlledOpen,
  onOpenChange,
  dropdownStyle,
  dropdownContainerStyle,
  showArrow = true,
  hideItemBorders = false,
}: CustomDropdownWebProps) => {
  const [isOpen, setIsOpen] = useState<boolean>(controlledOpen ?? false);
  const [isActive, setIsActive] = useState(false);
  const [hoveredOption, setHoveredOption] = useState<string | null>(null);
  const [menuPosition, setMenuPosition] = useState<DropdownMenuPosition | null>(null);
  const [focusedOptionIndex, setFocusedOptionIndex] = useState<number>(-1);
  const dropdownRef = useRef(null);
  const triggerRef = useRef<View>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const optionRefs = useRef<Array<any>>([]);
  const isWeb = Platform.OS === "web";
  const shouldRenderMenuInPortal = isWeb && typeof document !== "undefined";

  const readMenuPosition = useCallback((): DropdownMenuPosition | null => {
    if (!isWeb || typeof window === "undefined") {
      return null;
    }

    const dropdownElement = dropdownRef.current as HTMLElement | null;
    if (!dropdownElement) {
      return null;
    }

    const rect = dropdownElement.getBoundingClientRect();

    return openUpward
      ? {
          left: rect.left,
          width: rect.width,
          bottom: window.innerHeight - rect.top + 4,
        }
      : {
          left: rect.left,
          width: rect.width,
          top: rect.bottom + 4,
        };
  }, [isWeb, openUpward]);

  useEffect(() => {
    if (controlledOpen != null) {
      setIsOpen(controlledOpen);
      setMenuPosition(controlledOpen ? readMenuPosition() : null);
    }
  }, [controlledOpen, readMenuPosition]);

  const openState = isOpen;

  const setOpenState = useCallback(
    (next: boolean) => {
      setMenuPosition(next ? readMenuPosition() : null);
      setIsOpen(next);
      onOpenChange?.(next);
    },
    [onOpenChange, readMenuPosition]
  );

  const closeDropdown = useCallback(() => {
    setOpenState(false);
    setIsActive(false);
    setHoveredOption(null);
    setFocusedOptionIndex(-1);
    if (isWeb && triggerRef.current) {
      const el = triggerRef.current as unknown as HTMLElement;
      el?.focus?.();
    }
  }, [setOpenState, isWeb]);

  // RN style types don't support overflowY/overflowX, but web does.
  // Casting through unknown keeps us type-safe without introducing `any`.
  const optionListWebOverflowStyle = isWeb
    ? ({ overflowY: "auto", overflowX: "hidden" } as unknown as ViewStyle)
    : null;

  const combinedStyles = [
    styles.dropdownContainer,
    fullWidth ? styles.fullWidth : styles.fitContent,
    error && error.length > 0 ? styles.dropdownContainerWithErrorSpace : null,
    openState ? styles.dropdownContainerOpen : null,
    style,
  ];

  const selectedOption = useMemo(() => options.find((option) => option.value === value), [options, value]);

  const updateMenuPosition = useCallback(() => {
    if (!openState) {
      return;
    }

    setMenuPosition(readMenuPosition());
  }, [openState, readMenuPosition]);

  const handleSelect = useCallback(
    (option: string) => {
      onChange(option);
      setOpenState(false);
      setIsActive(true);
      setHoveredOption(null);
      setFocusedOptionIndex(-1);
      if (isWeb && triggerRef.current) {
        requestAnimationFrame(() => {
          const el = triggerRef.current as unknown as HTMLElement;
          el?.focus?.();
        });
      }
    },
    [onChange, setOpenState, isWeb]
  );

  const focusOption = useCallback((index: number) => {
    setFocusedOptionIndex(index);
    requestAnimationFrame(() => {
      const ref = optionRefs.current[index];
      if (ref) {
        const el = ref as unknown as HTMLElement;
        el?.focus?.();
      }
    });
  }, []);

  const openAndFocus = useCallback(
    (startIndex?: number) => {
      setOpenState(true);
      const idx = startIndex ?? (selectedOption ? options.indexOf(selectedOption) : 0);
      const targetIdx = Math.max(0, Math.min(idx, options.length - 1));
      setTimeout(() => {
        focusOption(targetIdx);
      }, 50);
    },
    [setOpenState, selectedOption, options, focusOption]
  );

  useEffect(() => {
    if (!isWeb || !openState || typeof window === "undefined" || typeof document === "undefined") {
      setMenuPosition(null);
      return;
    }

    updateMenuPosition();

    const handleViewportChange = () => {
      updateMenuPosition();
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closeDropdown();
      }
    };

    window.addEventListener("resize", handleViewportChange);
    window.addEventListener("scroll", handleViewportChange, true);
    document.addEventListener("keydown", handleEscape);

    return () => {
      window.removeEventListener("resize", handleViewportChange);
      window.removeEventListener("scroll", handleViewportChange, true);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [closeDropdown, isWeb, openState, updateMenuPosition]);

  const currentLabel = selectedOption ? selectedOption.label : placeholder;

  const buttonStyle = [
    styles.selectedValue,
    fullWidth ? styles.fullWidth : styles.fitContent,
    !fullWidth && styles.fillContainer,
    ...(disabled ? [styles.buttonDisabled] : []),
    ...(isActive ? (error != null && error !== "" ? [styles.buttonError] : [styles.buttonActive]) : []),
    dropdownStyle,
  ];

  const textStyle = selectedOption
    ? styles.textLabel
    : placeholderTextColor
      ? [styles.textPlaceholder, { color: placeholderTextColor }]
      : styles.textPlaceholder;

  const handleTriggerKeyDown = useCallback(
    // @ts-expect-error — onKeyDown is a web-only prop not in RN types
    (event: { key: string; preventDefault?: () => void }) => {
      if (disabled) return;
      switch (event.key) {
        case "Enter":
        case " ":
          event.preventDefault?.();
          if (!openState) {
            openAndFocus();
          } else {
            closeDropdown();
          }
          break;
        case "ArrowDown":
          event.preventDefault?.();
          if (!openState) {
            openAndFocus(0);
          } else {
            focusOption(0);
          }
          break;
        case "ArrowUp":
          event.preventDefault?.();
          if (!openState) {
            openAndFocus(options.length - 1);
          } else {
            focusOption(options.length - 1);
          }
          break;
        default:
          break;
      }
    },
    [disabled, openState, openAndFocus, closeDropdown, focusOption, options.length]
  );

  const handleOptionKeyDown = useCallback(
    (
      index: number,
      optionValue: string,
      // @ts-expect-error — onKeyDown is a web-only prop not in RN types
      event: { key: string; preventDefault?: () => void }
    ) => {
      switch (event.key) {
        case "ArrowDown": {
          event.preventDefault?.();
          const next = (index + 1) % options.length;
          focusOption(next);
          break;
        }
        case "ArrowUp": {
          event.preventDefault?.();
          const prev = (index - 1 + options.length) % options.length;
          focusOption(prev);
          break;
        }
        case "Home":
          event.preventDefault?.();
          focusOption(0);
          break;
        case "End":
          event.preventDefault?.();
          focusOption(options.length - 1);
          break;
        case "Enter":
        case " ":
          event.preventDefault?.();
          handleSelect(optionValue);
          break;
        case "Escape":
          event.preventDefault?.();
          closeDropdown();
          break;
        default: {
          if (event.key.length === 1) {
            const char = event.key.toLowerCase();
            const startFrom = (index + 1) % options.length;
            for (let i = 0; i < options.length; i++) {
              const idx = (startFrom + i) % options.length;
              if (options[idx].label.toLowerCase().startsWith(char)) {
                focusOption(idx);
                break;
              }
            }
          }
          break;
        }
      }
    },
    [options, focusOption, handleSelect, closeDropdown]
  );

  const optionListContent = (
    <View
      style={[styles.optionList, styles.optionListPortal, optionListWebOverflowStyle, dropdownContainerStyle]}
      role="listbox"
      aria-label={accessibilityLabel || placeholder}>
      {options.map((option, i) => {
        const isSelected = value === option.value;
        const isHovered = hoveredOption === option.value;
        const isFocused = focusedOptionIndex === i;

        return (
          <Pressable
            // @ts-expect-error — ref is used as HTMLElement for imperative focus
            ref={(el) => {
              optionRefs.current[i] = el;
            }}
            accessibilityLabel={option.label}
            accessibilityRole="none"
            key={option.value}
            role="option"
            aria-selected={isSelected}
            tabIndex={focusedOptionIndex === i ? 0 : -1}
            onPress={() => {
              handleSelect(option.value);
            }}
            onHoverIn={() => setHoveredOption(option.value)}
            onHoverOut={() => setHoveredOption(null)}
            onFocus={() => {
              setFocusedOptionIndex(i);
            }}
            onBlur={() => {
              setFocusedOptionIndex((currentIndex) => (currentIndex === i ? -1 : currentIndex));
            }}
            // @ts-expect-error — onKeyDown is a web-only prop not in RN types
            onKeyDown={(e: { key: string; preventDefault?: () => void }) => handleOptionKeyDown(i, option.value, e)}
            style={[
              styles.optionItem,
              hideItemBorders ? styles.optionItemNoBorder : null,
              isHovered ? styles.optionItemHover : null,
              isFocused ? (styles.optionItemFocused as ViewStyle) : null,
            ]}>
            {isSelected && (
              <View style={styles.checkIcon}>
                <CheckIcon size={16} style={{ height: 16, width: 16, color: "inherit" }} />
              </View>
            )}
            <Text style={styles.textLabel}>{option.label}</Text>
          </Pressable>
        );
      })}
    </View>
  );

  const portalWrapperStyle = menuPosition
    ? ({
        position: "fixed",
        left: menuPosition.left,
        width: menuPosition.width,
        zIndex: 100000,
        ...(menuPosition.top != null ? { top: menuPosition.top } : {}),
        ...(menuPosition.bottom != null ? { bottom: menuPosition.bottom } : {}),
      } as unknown as ViewStyle)
    : null;

  const portalBackdropStyle = isWeb
    ? ({
        position: "fixed",
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        backgroundColor: "transparent",
        zIndex: 99999,
      } as unknown as ViewStyle)
    : null;

  return (
    <>
      <View style={combinedStyles} ref={dropdownRef}>
        <Pressable
          ref={triggerRef}
          style={buttonStyle}
          onPress={() => {
            if (disabled) {
              return;
            }

            setOpenState(!openState);
            if (!openState) {
              setFocusedOptionIndex(-1);
            }
          }}
          // @ts-expect-error — onKeyDown is a web-only prop not in RN types
          onKeyDown={handleTriggerKeyDown}
          disabled={disabled}
          accessibilityLabel={accessibilityLabel || placeholder}
          accessibilityRole="combobox"
          aria-expanded={openState}
          aria-haspopup="listbox"
          aria-label={accessibilityLabel || placeholder}>
          <Text style={textStyle}>{currentLabel}</Text>
          {showArrow &&
            (openState ? (
              <CaretUpIcon style={styles.caretIcon} size={16} />
            ) : (
              <CaretDownIcon style={styles.caretIcon} size={16} />
            ))}
        </Pressable>

        <View
          style={styles.ghostContainer}
          accessibilityElementsHidden={true}
          importantForAccessibility="no-hide-descendants">
          <View style={styles.selectedValue}>
            <Text style={styles.textLabel}>{placeholder}</Text>
            <CaretDownIcon style={styles.caretIcon} size={16} />
          </View>
          {options.map((option) => (
            <View key={option.value} style={styles.selectedValue}>
              <Text style={styles.textLabel}>{option.label}</Text>
              <CaretDownIcon style={styles.caretIcon} size={16} />
            </View>
          ))}
        </View>

        {openState && !shouldRenderMenuInPortal ? (
          <View
            style={[
              styles.inlineOptionListWrapper,
              openUpward ? styles.inlineOptionListUpward : styles.inlineOptionListDownward,
            ]}>
            {optionListContent}
          </View>
        ) : null}
        {error && error.length > 0 ? <Text style={styles.errorText}>{error}</Text> : null}
      </View>

      {openState && shouldRenderMenuInPortal && menuPosition
        ? createPortal(
            <>
              <Pressable
                style={portalBackdropStyle}
                onPress={closeDropdown}
                accessibilityElementsHidden={true}
                importantForAccessibility="no-hide-descendants"
              />
              <View style={portalWrapperStyle}>{optionListContent}</View>
            </>,
            document.body
          )
        : null}
    </>
  );
};

const styles = StyleSheet.create({
  dropdownContainer: {
    position: "relative",
    zIndex: 0,
    elevation: 0,
  },
  fullWidth: {
    width: "100%",
  },
  fitContent: {
    alignSelf: "flex-start",
  },
  fillContainer: {
    width: "100%",
  },
  dropdownContainerWithErrorSpace: {
    marginBottom: 18,
  },
  dropdownContainerOpen: {
    zIndex: 9999,
    elevation: 9999,
  },
  selectedValue: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    padding: 12,
    backgroundColor: "white",
    borderWidth: 1,
    borderColor: "#d1d5db",
    borderRadius: 8,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 3,
    minHeight: 40,
  },
  buttonActive: {
    borderColor: "#4f46e5",
  },
  buttonDisabled: {
    backgroundColor: "#f3f4f6",
    borderColor: "#d1d5db",
  },
  buttonError: {
    borderColor: "#ef4444",
  },
  optionList: {
    padding: 4,
    backgroundColor: "white",
    borderWidth: 1,
    borderColor: "#e5e7eb",
    borderRadius: 8,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.1,
    shadowRadius: 12,
    maxHeight: 240,
    overflow: "hidden",
  },
  optionListPortal: {
    width: "100%",
  },
  optionItem: {
    maxHeight: 400,
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    paddingTop: 10,
    paddingBottom: 10,
    paddingRight: 16,
    paddingLeft: 30,
    borderRadius: 6,
    position: "relative",
    borderBottomWidth: 1,
    borderBottomColor: "#f3f4f6",
    width: "100%",
  },
  optionItemHover: {
    backgroundColor: "#f0f9ff",
  },
  optionItemFocused: {
    backgroundColor: "#eff6ff",
    outlineWidth: 2,
    outlineColor: "#4f46e5",
    outlineStyle: "solid",
    // @ts-expect-error — outlineOffset is a valid web CSS property not in RN StyleSheet types
    outlineOffset: -2,
  },
  optionItemNoBorder: {
    borderBottomWidth: 0,
  },
  checkIcon: {
    position: "absolute",
    left: 6,
    top: "50%",
    marginTop: -8,
    height: 16,
    width: 16,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  errorText: {
    position: "absolute",
    top: "100%",
    right: 0,
    left: 0,
    color: "#ef4444",
    fontSize: 12,
    marginTop: 4,
    marginLeft: 8,
  },
  caretIcon: {
    height: 16,
    width: 16,
    color: "#6b7280",
  },
  textLabel: {
    color: "#1f2937",
    fontWeight: "400",
    fontSize: 14,
  },
  textPlaceholder: {
    color: "#6b7280",
    fontSize: 14,
  },
  ghostContainer: {
    height: 0,
    overflow: "hidden",
    opacity: 0,
  },
  inlineOptionListWrapper: {
    position: "absolute",
    left: 0,
    right: 0,
    zIndex: 10000,
  },
  inlineOptionListDownward: {
    top: "100%",
    marginTop: 4,
  },
  inlineOptionListUpward: {
    bottom: "100%",
    marginBottom: 4,
  },
});
